// Employee-HR helpers — shared by the Employee list + editor.
// Backend: Xano /employee (api:Bh0CIPqU), proxied at /api/hr/employees*.
// Generic date/money/branch helpers are reused from asset.js (single source).

export { parseDate, toDateInput, fmtDateTH, fmtMoney, relabelBranch } from './asset.js'

/* ── enum choices (verbatim from the Employee table schema) ──────────────── */
export const PREFIXES = ['นาย', 'น.ส.', 'นาง']

// ⚠️ NOT the list any more — the FALLBACK list. Until 2026-08-19 these twelve were
// the `position` enum on the Employee table, so adding one meant a Xano schema edit
// and a deploy. They are rows in `employee_position` now (GET /api/hr/positions),
// seeded from exactly this array, and `Employee.position` is plain text holding a
// row's name. This copy stays for the two cases where a fetch is not available or
// not worth it: a catalog request that failed (the editor must still open), and the
// screens that only ever need to GROUP by position and would gain nothing from a
// round-trip. Adding a position here does NOT add it to the dropdown — the catalog does.
export const POSITIONS = [
  'Counter', 'Admin', 'ผู้ช่วยทันตแพทย์', 'ช่างทันตกรรม', 'ผู้จัดการ', 'Graphic',
  'Messenger', 'CEO', 'HR', 'Sale', 'แม่บ้าน', 'อื่นๆ',
]

// "Is this the same position?" — trims, collapses inner whitespace, lower-cases.
// Thai has no case, but "Counter"/"counter" is one job and two rows would split
// every roster count and evaluation rule that keys on the string. Mirrors the
// duplicate check in the Xano POST /employee_position.
export const positionKey = (p) => String(p ?? '').trim().replace(/\s+/g, ' ').toLowerCase()

// The dropdown's list: the catalog, in its own order, plus the value the record
// already holds when that is off-catalog. Same reasoning as bankOptions() in
// EmployeeEditor — a position retired after this person was hired, or renamed
// under them, would otherwise render blank and the next save would wipe it. It is
// shown so the fact is visible rather than silently corrected.
export function positionChoices(catalog, current) {
  const names = (Array.isArray(catalog) ? catalog : [])
    .filter((p) => p && p.active !== false && String(p.name || '').trim())
    .map((p) => String(p.name).trim())
  const list = names.length ? names : [...POSITIONS]
  const held = String(current || '').trim()
  return held && !list.some((n) => positionKey(n) === positionKey(held)) ? [held, ...list] : list
}

// Roster-sized labels for the position enum. The full titles are correct on an
// HR record and unreadable in a column header or a day's headcount line, which
// is where a manager reads them ("ผู้ช่วย 4 · Counter 3 · ผู้จัดการ 1").
export const POSITION_SHORT = {
  'ผู้ช่วยทันตแพทย์': 'ผู้ช่วย',
  'ช่างทันตกรรม': 'ช่างทันต',
  'Messenger': 'Msg',
}
export const positionShort = (p) => POSITION_SHORT[String(p || '')] || String(p || '') || 'ไม่ระบุตำแหน่ง'

// role is a multi-value list. `HR` here is what unlocks the salary/note sections
// and the Salary/Recurring tabs (surfaced into me.roles as `hr`).
export const ROLES = ['Admin', 'Assistance', 'Doctor', 'HR', 'Counter', 'Lab', 'GM', 'Manager', 'Stock Manager']

// Roles the employee editor may NOT grant. Both still SHOW in the editor when an
// account already holds one (locked), so the fact is never hidden; granting or
// revoking either is a deliberate act elsewhere.
//   Admin  — unlocks every gate in the app: salary, payroll, the question-bank,
//            patient records, and the ability to hand Admin to somebody else. It
//            should not be one mis-click away in a dialog HR uses daily.
//   Doctor — a dentist is a row in the Doctor table plus a `doctor_id` on the user
//            account, neither of which this dialog writes. Ticking the chip creates
//            no dentist; it only hands out the clinical-edit gate (role.js
//            #canEditClinical) and drops the person out of the lab-only confine.
//            Doctor identity is managed from the doctor portal, not from an HR record.
export const LOCKED_ROLES = ['Admin', 'Doctor']

// Roles the employee editor may GRANT.
export const ASSIGNABLE_ROLES = ROLES.filter((r) => !LOCKED_ROLES.includes(r))

export const BANKS = ['ออมสิน', 'ทหารไทย', 'กรุงเทพ', 'กรุงไทย', 'กสิกรไทย', 'ไทยพาณิชย์', 'กรุงศรีอยุธยา', 'ยูโอบี']

/* ── field normalisers ───────────────────────────────────────────────────── */

// phone / idcardnumber / bank_branchcode / bank_num all carry `digitOk` in the
// Xano schema — the column rejects anything else, so the input strips it rather
// than letting a pasted "081-234-5678" travel to the server to be refused there.
export const digitsOnly = (v) => String(v ?? '').replace(/\D/g, '')

export const PHONE_LEN = 10
export const IDCARD_LEN = 13

// รหัสพนักงาน is not typed in — it IS "<ชื่อเล่น> <ชื่อจริง>", the form every
// screen in the app reads a person by (the picker labels, the clockin roster,
// the payroll export). Deriving it removes the two ways it used to go wrong:
// a hand-typed variant that no longer matches the person's name, and the older
// rows that used a "หมิว - เพ็ญนภา" dash instead of a space.
// Only the FIRST token of `name` is used — the surname is not part of the code.
export function employeeCode(nickname, name) {
  const first = String(name || '').trim().split(/\s+/)[0] || ''
  return [String(nickname || '').trim(), first].filter(Boolean).join(' ')
}

/* ── display helpers ─────────────────────────────────────────────────────── */
export function fullName(e) {
  if (!e) return ''
  const base = [e.prefix, e.name].filter(Boolean).join('')
  return base || e.name || e.nickname || e.email || '—'
}

export const roleList = (e) => (Array.isArray(e?.role) ? e.role : (e?.role ? [e.role] : []))

// Named salary entries the payroll reads.
export function salaryEntry(e, name) {
  const list = Array.isArray(e?.salary) ? e.salary : []
  return list.find((s) => s && s.name === name) || null
}
export const salaryBase = (e) => Number(salaryEntry(e, 'Base Salary')?.amount || 0)
export function salaryTotal(e) {
  const list = Array.isArray(e?.salary) ? e.salary : []
  return list.reduce((sum, s) => sum + Number(s?.amount || 0), 0)
}

// id→relabelled name map for branch <select>s and table cells.
export function indexBranches(branches) {
  const m = new Map()
  for (const b of Array.isArray(branches) ? branches : []) m.set(Number(b.id), b.name)
  return m
}

// active pill tone.
export function activeInfo(active) {
  return active
    ? { label: 'ใช้งาน', tone: 'bg-emerald-50 text-emerald-700 ring-emerald-200' }
    : { label: 'ปิดใช้งาน', tone: 'bg-slate-100 text-slate-500 ring-slate-200' }
}

/* ── probation ────────────────────────────────────────────────────────────── */

// Local (Asia/Bangkok) today as YYYY-MM-DD. Built from LOCAL parts on purpose: toISOString
// would give UTC, which for the last seven hours of every Thai day names yesterday — and
// this is compared against a date-only column where that is a whole day of error.
export function todayYMD(now = new Date()) {
  const pad = (n) => String(n).padStart(2, '0')
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}

// Is this employee still inside their probation window?
//
// Employee.date_probationpass is set FORWARD at hire time — it is the scheduled end of
// probation, not a retroactive sign-off — so a date today or later means they have not
// reached it yet, and a past date means they came through it.
//
// A NULL date_probationpass is deliberately NOT "on probation": 150 of the 383 employee
// records carry null, overwhelmingly legacy Bubble rows for long-tenured staff whose dates
// were never backfilled. Reading null as on-probation would flag ~40% of the company.
//
// Compared as YYYY-MM-DD strings, which sort identically to the dates themselves — no Date
// parsing, so no timezone drift on a date-only field.
export function isOnProbation(e, today = todayYMD()) {
  return !!e?.date_probationpass && String(e.date_probationpass) >= today
}
